import {
  ANY_TIME_OF_DAY,
  patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions,
} from "@/api/Activities";
import { deducedError } from "@/Utils/ErrorUtils";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { getBasePlanData } from "./getBasePlanData";
import type { MessageDescriptor } from "@lingui/core";
import { msg, t } from "@lingui/core/macro";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { machineFormat } from "@medoma/models/date-and-time";
import { Frequency } from "../ActivityOccurrence/ActivitySchedule";
import {
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
} from "react-aria-components";
import styles from "./BasePlan.module.scss";
import {
  orderedCategories,
  timeOfDayDictionary,
} from "@medoma/models/activities";
import clsx from "clsx";
import NoResults from "@/components/NoResults/NoResults";
import { useParentRoutesPatient } from "@/api/Patients";
import { getPatientFullName } from "@medoma/models/patients";

const columns: {
  name: MessageDescriptor;
  id: keyof ReturnType<typeof getBasePlanData>[0];
  isRowHeader?: true;
}[] = [
  { name: msg`Typ`, id: "category" },
  { name: msg`Aktivitet`, id: "title", isRowHeader: true },
  { name: msg`Kompetens`, id: "requiredCompetences" },
  { name: msg`Schemaläggning`, id: "schedule" },
  { name: msg`Startdatum`, id: "startDate" },
  { name: msg`Starttid`, id: "startTimes" },
  { name: msg`Avslutas`, id: "endDate" },
];

const formatCell = (
  item: ReturnType<typeof getBasePlanData>[0],
  key: keyof ReturnType<typeof getBasePlanData>[0],
) => {
  switch (key) {
    case "category":
      return <CategoryIcon category={item[key]} size="small" />;
    case "title":
      return item[key];
    case "requiredCompetences": {
      const competences = item[key];
      if (competences === undefined) {
        return null;
      }
      return (
        <div className={styles.competences}>
          {competences.map((competence) => (
            <CompetenceChip
              key={competence}
              competence={competence}
              state="neutral"
            />
          ))}
        </div>
      );
    }
    case "schedule":
      return <Frequency schedule={item[key]} />;
    case "startDate":
      return item[key];
    case "startTimes":
      if (item[key] === ANY_TIME_OF_DAY) {
        return t(timeOfDayDictionary.Any.short);
      }
      return Array.isArray(item[key]) ? item[key].join(", ") : item[key];
    case "endDate": {
      const endDate = item[key];
      if (!endDate) {
        return null;
      }
      return machineFormat(endDate, "yyyy-MM-dd");
    }
    // ´id´ is only used for navigation and not displayed in the table
    case "id":
      return null;
    default:
      return key satisfies never;
  }
};

const BasePlan = () => {
  const { t } = useLingui();
  const { id: patientId, name } = useParentRoutesPatient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    data: activities,
    isPending,
    isError,
    error,
  } = useQuery(
    patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions({
      patientId,
    }),
  );

  if (isPending) {
    return <Loading message={t`Hämtar grundplanering`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta grundplanering.`} ${deducedError(error)}`}
      />
    );
  }

  if (activities.length === 0) {
    const patientName = getPatientFullName(name);
    return <NoResults message={t`${patientName} har inga aktiviteter`} />;
  }

  // Sort activities by orderedCategories
  const sortedActivities = [...activities].sort((a, b) => {
    const aState = a.category;
    const bState = b.category;
    return (
      orderedCategories.indexOf(aState) - orderedCategories.indexOf(bState)
    );
  });

  const rows = getBasePlanData(sortedActivities);

  const rowIsActive = (id: string) =>
    pathname.endsWith(`/commandcenter/patients/${patientId}/activities/${id}`);

  return (
    <Table className={styles.basePlanTable} aria-label={t`Grundplanering`}>
      <TableHeader columns={columns}>
        {(column) => (
          <Column isRowHeader={column.isRowHeader}>{t(column.name)}</Column>
        )}
      </TableHeader>
      <TableBody items={rows}>
        {(item) => {
          return (
            <Row
              id={item.id}
              className={clsx([rowIsActive(item.id) && styles.activeRow])}
              columns={columns}
              onAction={() => navigate(`${item.id}`)}
            >
              {(column) => <Cell>{formatCell(item, column.id)}</Cell>}
            </Row>
          );
        }}
      </TableBody>
    </Table>
  );
};

export default BasePlan;
